<template>
  <ValidationObserver class="row" :vid="id">
    <ValidationProvider
      name="password"
      :bails="false"
      :rules="rules"
      v-slot="{ touched, failedRules, failed, invalid, changed }"
      class="field col-12 col-md-6"
    >
      <div>
        <input
          role="textbox"
          :aria-label="name"
          v-model="password"
          :type="showPassword ? 'text' : 'password'"
          :id="id"
          :name="name"
          class="field__input"
          :class="failed && (failedRules.required || touched) ? 'border-danger' : ''"
          autocomplete="off"
          placeholder=" "
          data-testid="password"
        />
        <label
          :for="id"
          class="field__label"
          :class="failed && password && touched ? 'text-danger' : ''"
          :disabled="disabled"
          >{{ translate(passwordPlacholder) }}</label
        >
      </div>
      <div v-if="failedRules.required" class="field__error">
        {{ translate('rwThisIsRequired') }}
      </div>
      <div v-if="!failedRules.required && touched && invalid" class="field__error">
        {{ translate('rwInvalidFormat') }}
      </div>
      <Checkbox
        :label="translate('rwPasswordShow')"
        :checked="false"
        @changed="onShowPasswordChanged"
        class="d-none d-md-block pass-show"
      />
      <span>{{ updateDataError(failedRules, changed) }}</span>
      <div v-if="changed && showPasswordValidationRules" class="d-none d-md-block">
        <PasswordValidationText :errorType="dataErrorType" />
      </div>
    </ValidationProvider>
    <ValidationProvider
      :bails="false"
      name="passwordRepeat"
      rules="required|password:@password"
      v-slot="{ touched, failedRules, failed }"
      class="field col-12 col-md-6"
      v-if="showRepeatPassword"
    >
      <input
        role="textbox"
        :aria-label="`confirm-${name}`"
        v-model="confirm"
        :type="showPassword ? 'text' : 'password'"
        class="field__input"
        :class="failed && (failedRules.required || touched) ? 'border-danger' : ''"
        autocomplete="off"
        placeholder=" "
        data-testid="confirm"
        :id="`confirm-${id}`"
      />
      <label
        :for="`confirm-${id}`"
        class="field__label"
        :class="failed && confirm && touched ? 'text-danger' : ''"
        :disabled="disabled"
        >{{ translate(repeatPasswordPlacholder) }}</label
      >
      <div v-if="failed" class="field__error">
        <div v-if="failedRules.required">{{ translate('rwThisIsRequired') }}</div>
        <div v-else-if="failedRules.password && touched">
          {{ translate('rwPasswordRepeatMustMatch') }}
        </div>
      </div>
    </ValidationProvider>
    <Checkbox
      :label="translate('rwPasswordShow')"
      :checked="false"
      @changed="onShowPasswordChanged"
      class="d-md-none pl-4 pass-show"
    />
    <PasswordValidationText
      :errorType="dataErrorType"
      class="col-12 mt-3 d-block d-md-none"
      v-if="showPasswordValidationRules && changed && dataErrorType !== null"
    />
  </ValidationObserver>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import Checkbox from '@/components/form-controls/Checkbox'
import PasswordValidationText from './PasswordValidationText.vue'
import { isFlagActive } from '@/infrastructure/Configuration'

export default {
  name: 'PasswordField',
  components: {
    ValidationObserver,
    ValidationProvider,
    Checkbox,
    PasswordValidationText
  },
  props: {
    id: {
      type: String,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: ' '
    },
    passwordPlacholder: {
      type: String,
      default: 'rwPassword'
    },
    repeatPasswordPlacholder: {
      type: String,
      default: 'rwPasswordRepeat'
    },
    showRepeatPassword: {
      type: Boolean,
      default: true
    },
    showPasswordValidationRules: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    password: '',
    confirm: '',
    showPassword: false,
    dataErrorType: {},
    changed: false,
    disabled: false,
    rules: ''
  }),
  methods: {
    isFlagActive,
    onShowPasswordChanged(checked) {
      this.showPassword = checked
    },
    updateDataError(errorsFromObserver, changed) {
      this.dataErrorType = errorsFromObserver
      this.changed = changed
    }
  },
  created() {
    if (this.id !== 'old-password') {
      this.rules = this.isFlagActive('private.password.special.character')
        ? 'required|length|containsNumber|containsUpperCase|containsLowerCase|containsSpecialCharacter'
        : 'required|length|containsNumber|containsUpperCase|containsLowerCase'
    }
  }
}
</script>

<style lang="scss" scoped>
$color-grey: #c2c3c5;

.field {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 1rem 1.5rem;
  width: 100%;
  row-gap: 0.3rem;

  &__input {
    position: relative;
    font-size: 1.4rem;
    padding: 0.75rem 1rem;
    border: solid 1px $color-grey;
    border-radius: 0.4rem;
    width: 100%;
  }

  &__label {
    position: absolute;
    background: white;
    top: 1.85rem;
    left: 2.5rem;
    font-size: 1.4rem;
    transition: top 200ms ease-in, left 200ms ease-in, font-size 200ms ease-in;
  }
  &__error {
    color: crimson;
    font-size: 1.1rem;
  }
}

.field__input:focus ~ .field__label,
.field__input:not(:placeholder-shown).field__input:not(:focus) ~ .field__label {
  top: 0;
  font-size: 1.1rem;
}

.input-icon {
  position: absolute !important;
  margin-right: 2.5rem !important;
  right: 0 !important;
}
input::-ms-reveal,
input::-ms-clear {
  display: none;
}
input:focus {
  outline: none;
}
html[dir='rtl'] {
  .field__label {
    right: 2.5rem;
    left: 0;
    width: fit-content;
  }
}
</style>
